import { TouchEvent, useRef, useState, type RefObject } from "react";

const MAX_PULL_DISTANCE = 84;
const REFRESH_THRESHOLD = 56;

export const usePullToRefresh = <T extends HTMLElement>(
  rootRef: RefObject<T | null>,
  onRefresh: () => Promise<void> | void,
  disabled = false,
) => {
  const pullStartYRef = useRef<number | null>(null);
  const pullDistanceRef = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const finishPull = () => {
    const shouldRefresh = pullDistanceRef.current >= REFRESH_THRESHOLD;
    pullStartYRef.current = null;
    pullDistanceRef.current = 0;
    setPullDistance(0);

    if (!shouldRefresh || disabled || refreshing) return;

    setRefreshing(true);
    Promise.resolve(onRefresh()).finally(() => setRefreshing(false));
  };

  const onTouchStart = (event: TouchEvent<T>) => {
    if (disabled || refreshing || rootRef.current?.scrollTop !== 0) return;
    pullStartYRef.current = event.touches[0]?.clientY ?? null;
  };

  const onTouchMove = (event: TouchEvent<T>) => {
    if (pullStartYRef.current === null || rootRef.current?.scrollTop !== 0) return;

    const distance = (event.touches[0]?.clientY ?? pullStartYRef.current) - pullStartYRef.current;
    if (distance <= 0) return;

    const nextDistance = Math.min(MAX_PULL_DISTANCE, Math.round(distance * 0.42));
    pullDistanceRef.current = nextDistance;
    setPullDistance(nextDistance);
  };

  return {
    pullDistance,
    refreshing,
    onTouchStart,
    onTouchMove,
    onTouchEnd: finishPull,
    onTouchCancel: finishPull,
    readyToRefresh: pullDistance >= REFRESH_THRESHOLD,
  };
};